import React, { useEffect, useState, useCallback, useRef } from "react";
import { api, fmtPrice, mediaUrl, apiError } from "@/lib/api";
import { useLang } from "@/context/LangContext";
import { statusLabel, statusColor } from "@/lib/status";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { ProductForm } from "@/components/ProductForm";
import { LocationCascade } from "@/components/LocationCascade";
import { LocationEditor } from "@/components/LocationEditor";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { CategoryManager } from "@/components/CategoryManager";
import { Users, Store, Truck, Package, ShoppingCart, Wallet, Shield, Plus, Pencil, Trash2, Upload, MapPin, LayoutGrid, Loader2 } from "lucide-react";
import { OrderTimeline } from "@/components/OrderTimeline";
import { STATUS_FLOW } from "@/lib/status";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";

export default function AdminPanel() {
  const { t } = useLang();
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [products, setProducts] = useState([]);
  const [orders, setOrders] = useState([]);
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [lPage, setLPage] = useState(1);
  const [uPage, setUPage] = useState(1);
  const [oPage, setOPage] = useState(1);
  const [orderView, setOrderView] = useState(null);
  const [savingStatus, setSavingStatus] = useState(false);

  const changeOrderStatus = async (status) => {
    if (!orderView || status === orderView.status) return;
    setSavingStatus(true);
    try {
      const { data } = await api.put(`/orders/${orderView.id}/status`, { status });
      setOrderView(data);
      setOrders((list) => list.map((o) => (o.id === data.id ? data : o)));
      toast.success("Statut mis à jour");
    } catch (e) { toast.error(apiError(e)); } finally { setSavingStatus(false); }
  };

  const assignCourier = async (courierId) => {
    if (!orderView) return;
    const cid = courierId === "none" ? null : courierId;
    if ((orderView.courier_id || null) === cid) return;
    try {
      const { data } = await api.put(`/admin/orders/${orderView.id}/courier`, { courier_id: cid });
      setOrderView(data);
      setOrders((list) => list.map((o) => (o.id === data.id ? data : o)));
      toast.success(cid ? "Livreur assigné" : "Livreur retiré");
    } catch (e) { toast.error(apiError(e)); }
  };

  const load = useCallback(async () => {
    const [s, u, p, o] = await Promise.all([
      api.get("/admin/stats"), api.get("/admin/users"), api.get("/admin/products"), api.get("/admin/orders"),
    ]);
    setStats(s.data); setUsers(u.data); setProducts(p.data); setOrders(o.data);
  }, []);
  useEffect(() => { load(); }, [load]);

  const toggleApproval = async (id) => {
    try { await api.put(`/admin/products/${id}/approval`); load(); }
    catch (e) { toast.error(apiError(e)); }
  };

  const deleteProduct = async (id) => {
    try { await api.delete(`/products/${id}`); toast.success(t("delete")); load(); }
    catch (e) { toast.error(apiError(e)); }
  };

  const changeRole = async (id, role) => {
    try { await api.put(`/admin/users/${id}/role`, { role }); toast.success(t("role_updated")); load(); }
    catch (e) { toast.error(apiError(e)); }
  };

  const toggleSuspend = async (u) => {
    try { const { data } = await api.put(`/admin/users/${u.id}/suspend`); toast.success(data.suspended ? t("suspend_done") : t("reactivate_done")); load(); }
    catch (e) { toast.error(apiError(e)); }
  };

  const statCards = [
    { icon: Users, k: "total_users", v: stats?.users }, { icon: Store, k: "total_sellers", v: stats?.sellers },
    { icon: Truck, k: "total_couriers", v: stats?.couriers }, { icon: Package, k: "stats_products", v: stats?.products },
    { icon: ShoppingCart, k: "stats_orders", v: stats?.orders }, { icon: Wallet, k: "stats_revenue", v: stats ? fmtPrice(stats.revenue) : "—" },
  ];

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 py-10">
      <h1 className="font-display font-extrabold text-3xl tracking-tight mb-8 flex items-center gap-2"><Shield className="w-7 h-7 text-emerald-800" /> {t("admin_panel")}</h1>

      <div className="grid grid-cols-2 lg:grid-cols-6 gap-3 mb-8">
        {statCards.map(({ icon: Icon, k, v }) => (
          <div key={k} className="bg-white border border-stone-200 rounded-xl p-4">
            <Icon className="w-5 h-5 text-emerald-800 mb-2" />
            <p className="text-xl font-display font-extrabold">{v ?? "—"}</p>
            <p className="text-[11px] text-stone-500 uppercase tracking-wider">{t(k)}</p>
          </div>
        ))}
      </div>

      <Tabs defaultValue="listings">
        <TabsList>
          <TabsTrigger value="listings" data-testid="tab-listings">{t("manage_listings")} {stats?.pending ? `(${stats.pending})` : ""}</TabsTrigger>
          <TabsTrigger value="categories" data-testid="tab-categories">{t("manage_categories")}</TabsTrigger>
          <TabsTrigger value="users" data-testid="tab-users">{t("manage_users")}</TabsTrigger>
          <TabsTrigger value="orders" data-testid="tab-admin-orders">{t("nav_orders")}</TabsTrigger>
          <TabsTrigger value="localisations" data-testid="tab-localisations">{t("manage_localisations")}</TabsTrigger>
          <TabsTrigger value="home" data-testid="tab-home">Page d'accueil</TabsTrigger>
        </TabsList>

        <TabsContent value="listings" className="mt-5">
          <div className="flex justify-end mb-3">
            <Button onClick={() => { setEditing(null); setFormOpen(true); }} className="rounded-full bg-mango-600 hover:bg-mango-700" data-testid="admin-add-product-btn"><Plus className="w-4 h-4 mr-1" />{t("add_product")}</Button>
          </div>
          <div className="bg-white border border-stone-200 rounded-xl overflow-hidden overflow-x-auto">
            <Table>
              <TableHeader><TableRow><TableHead>{t("image")}</TableHead><TableHead>{t("product_name")}</TableHead><TableHead>{t("price")}</TableHead><TableHead>{t("approved")}</TableHead><TableHead className="text-right">{t("edit")}</TableHead></TableRow></TableHeader>
              <TableBody>
                {products.slice((lPage - 1) * PAGE_SIZE, lPage * PAGE_SIZE).map((p) => (
                  <TableRow key={p.id} data-testid={`admin-product-${p.id}`}>
                    <TableCell><img src={mediaUrl(p.image)} alt="" className="w-10 h-10 rounded object-cover" /></TableCell>
                    <TableCell><div className="font-medium">{p.name}</div><div className="text-xs text-stone-400">{p.seller_name}</div></TableCell>
                    <TableCell className="font-semibold">{fmtPrice(p.price)}</TableCell>
                    <TableCell><Switch checked={p.approved} onCheckedChange={() => toggleApproval(p.id)} data-testid={`approve-switch-${p.id}`} /></TableCell>
                    <TableCell>
                      <div className="flex items-center justify-end gap-2">
                        <Button size="sm" variant="outline" onClick={() => { setEditing(p); setFormOpen(true); }} data-testid={`admin-edit-${p.id}`}><Pencil className="w-3.5 h-3.5" /></Button>
                        <Button size="sm" variant="outline" className="text-red-600 border-red-200" onClick={() => deleteProduct(p.id)} data-testid={`admin-delete-${p.id}`}><Trash2 className="w-3.5 h-3.5" /></Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            <Pager page={lPage} total={products.length} onPage={setLPage} testid="listings-pagination" />
          </div>
          <ProductForm open={formOpen} setOpen={setFormOpen} product={editing} onSaved={load} />
        </TabsContent>

        <TabsContent value="categories" className="mt-5">
          <CategoryManager />
        </TabsContent>

        <TabsContent value="users" className="mt-5">
          <div className="bg-white border border-stone-200 rounded-xl overflow-hidden overflow-x-auto">
            <Table>
              <TableHeader><TableRow><TableHead>{t("name")}</TableHead><TableHead>{t("email")}</TableHead><TableHead>{t("phone")}</TableHead><TableHead>{t("role")}</TableHead><TableHead>{t("user_status")}</TableHead><TableHead className="text-right">{t("actions")}</TableHead></TableRow></TableHeader>
              <TableBody>
                {users.slice((uPage - 1) * PAGE_SIZE, uPage * PAGE_SIZE).map((u) => (
                  <TableRow key={u.id} data-testid={`admin-user-${u.id}`}>
                    <TableCell className="font-medium">{u.name}</TableCell>
                    <TableCell className="text-stone-500">{u.email}</TableCell>
                    <TableCell className="text-stone-500">{u.phone}</TableCell>
                    <TableCell>
                      {u.role === "admin" ? (
                        <span className="text-xs font-semibold px-2.5 py-1 rounded-full bg-emerald-50 text-emerald-800">admin</span>
                      ) : (
                        <Select value={u.role} onValueChange={(v) => changeRole(u.id, v)}>
                          <SelectTrigger className="w-32 h-8" data-testid={`role-select-${u.id}`}><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="buyer">{t("role_buyer")}</SelectItem>
                            <SelectItem value="seller">{t("role_seller")}</SelectItem>
                            <SelectItem value="delivery">{t("role_delivery")}</SelectItem>
                          </SelectContent>
                        </Select>
                      )}
                    </TableCell>
                    <TableCell>
                      {u.suspended ? (
                        <span className="text-xs font-semibold px-2.5 py-1 rounded-full bg-red-50 text-red-600" data-testid={`status-${u.id}`}>{t("status_suspended")}</span>
                      ) : (
                        <span className="text-xs font-semibold px-2.5 py-1 rounded-full bg-emerald-50 text-emerald-700" data-testid={`status-${u.id}`}>{t("status_active")}</span>
                      )}
                    </TableCell>
                    <TableCell className="text-right">
                      {u.role !== "admin" && (
                        <Button size="sm" variant="outline" className={u.suspended ? "text-emerald-700 border-emerald-200" : "text-red-600 border-red-200"} onClick={() => toggleSuspend(u)} data-testid={`suspend-btn-${u.id}`}>
                          {u.suspended ? t("reactivate") : t("suspend")}
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            <Pager page={uPage} total={users.length} onPage={setUPage} testid="users-pagination" />
          </div>
        </TabsContent>

        <TabsContent value="orders" className="mt-5">
          <div className="bg-white border border-stone-200 rounded-xl overflow-hidden overflow-x-auto">
            <Table>
              <TableHeader><TableRow><TableHead>{t("order_no")}</TableHead><TableHead>Client</TableHead><TableHead>{t("total")}</TableHead><TableHead>Status</TableHead><TableHead className="text-right">{t("actions")}</TableHead></TableRow></TableHeader>
              <TableBody>
                {orders.slice((oPage - 1) * PAGE_SIZE, oPage * PAGE_SIZE).map((o) => (
                  <TableRow key={o.id} className="cursor-pointer hover:bg-stone-50" onClick={() => setOrderView(o)} data-testid={`admin-order-row-${o.id}`}>
                    <TableCell className="font-medium">{o.order_no}</TableCell>
                    <TableCell className="text-stone-500">{o.buyer_name}</TableCell>
                    <TableCell className="font-semibold">{fmtPrice(o.total)}</TableCell>
                    <TableCell><span className={`text-xs font-semibold px-2.5 py-1 rounded-full ${statusColor(o.status)}`}>{statusLabel(t, o.status)}</span></TableCell>
                    <TableCell className="text-right"><Button size="sm" variant="outline" className="rounded-full" onClick={(e) => { e.stopPropagation(); setOrderView(o); }} data-testid={`view-order-${o.id}`}>Voir</Button></TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            <Pager page={oPage} total={orders.length} onPage={setOPage} testid="orders-pagination" />
          </div>
        </TabsContent>

        <TabsContent value="localisations" className="mt-5">
          <AdminLocalisations t={t} />
        </TabsContent>

        <TabsContent value="home" className="mt-5">
          <AdminHomeLayout t={t} />
        </TabsContent>
      </Tabs>

      <Dialog open={!!orderView} onOpenChange={(v) => !v && setOrderView(null)}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="admin-order-dialog">
          {orderView && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-3 flex-wrap">
                  {orderView.order_no}
                  <span className={`text-xs font-semibold px-2.5 py-1 rounded-full ${statusColor(orderView.status)}`}>{statusLabel(t, orderView.status)}</span>
                </DialogTitle>
              </DialogHeader>

              <div className="flex items-center gap-3 flex-wrap bg-stone-50 border border-stone-200 rounded-xl p-3">
                <span className="text-sm font-semibold">Statut :</span>
                <Select value={orderView.status} onValueChange={changeOrderStatus}>
                  <SelectTrigger className="w-56 h-9" data-testid="admin-order-status-select"><SelectValue /></SelectTrigger>
                  <SelectContent>{STATUS_FLOW.map((s) => <SelectItem key={s} value={s}>{statusLabel(t, s)}</SelectItem>)}</SelectContent>
                </Select>
                {savingStatus && <Loader2 className="w-4 h-4 animate-spin text-emerald-800" />}
              </div>

              <div className="grid sm:grid-cols-2 gap-3 text-sm">
                <div className="bg-white border border-stone-200 rounded-xl p-3">
                  <p className="text-xs text-stone-400 mb-1">Client</p>
                  <p className="font-medium">{orderView.buyer_name}</p>
                  <p className="text-stone-500 text-xs break-all">{orderView.buyer_email}</p>
                </div>
                <div className="bg-white border border-stone-200 rounded-xl p-3">
                  <p className="text-xs text-stone-400 mb-1">Paiement</p>
                  <p className="font-medium">{orderView.payment_method === "cash" ? "Paiement à la livraison" : "Mobile Money"}</p>
                  <p className="text-stone-500 text-xs">{orderView.payment_status === "paid" ? "Payé" : "En attente"}</p>
                </div>
              </div>

              <div className="bg-white border border-stone-200 rounded-xl p-3 text-sm">
                <p className="text-xs text-stone-400 mb-1">Adresse de livraison</p>
                <p className="font-medium">{orderView.address?.full_name} · {orderView.address?.phone}</p>
                <p className="text-stone-500">{orderView.address?.street} {orderView.address?.district}, {orderView.address?.city}</p>
                {orderView.courier_name && <p className="text-emerald-700 text-xs mt-1">Livreur : {orderView.courier_name}</p>}
              </div>

              <div className="bg-white border border-stone-200 rounded-xl p-3 text-sm">
                <p className="text-xs text-stone-400 mb-2">Livreur assigné</p>
                <Select value={orderView.courier_id || "none"} onValueChange={assignCourier}>
                  <SelectTrigger className="h-9" data-testid="admin-order-courier-select"><SelectValue placeholder="Non assigné" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Non assigné</SelectItem>
                    {users.filter((u) => u.role === "delivery").map((c) => <SelectItem key={c.id} value={c.id}>{c.name} · {c.email}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>

              <div className="bg-white border border-stone-200 rounded-xl divide-y divide-stone-100" data-testid="admin-order-items">
                {orderView.items.map((it) => (
                  <div key={it.product_id} className="flex items-center gap-3 p-3">
                    <img src={mediaUrl(it.image)} alt={it.name} className="w-12 h-12 rounded-lg object-cover bg-stone-100" />
                    <div className="flex-1 min-w-0"><p className="text-sm font-medium line-clamp-1">{it.name}</p><p className="text-xs text-stone-400">{it.quantity} × {fmtPrice(it.price)}</p></div>
                    <p className="text-sm font-semibold">{fmtPrice(it.price * it.quantity)}</p>
                  </div>
                ))}
                <div className="p-3 text-sm space-y-1">
                  <div className="flex justify-between text-stone-500"><span>Sous-total</span><span>{fmtPrice(orderView.subtotal)}</span></div>
                  <div className="flex justify-between text-stone-500"><span>Livraison</span><span>{fmtPrice(orderView.shipping_fee)}</span></div>
                  <div className="flex justify-between font-display font-extrabold text-emerald-800 text-base pt-1"><span>Total</span><span>{fmtPrice(orderView.total)}</span></div>
                </div>
              </div>

              <div className="bg-white border border-stone-200 rounded-xl p-4">
                <p className="text-sm font-semibold mb-3">Parcours de la commande</p>
                <OrderTimeline status={orderView.status} />
                {orderView.timeline?.length > 0 && (
                  <div className="mt-4 pt-3 border-t border-stone-100 space-y-1">
                    {orderView.timeline.map((e, i) => (
                      <p key={i} className="text-xs text-stone-500 flex justify-between gap-2"><span>{statusLabel(t, e.status)}</span><span>{new Date(e.at).toLocaleString()}</span></p>
                    ))}
                  </div>
                )}
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

const PAGE_SIZE = 10;

const SORT_OPTIONS = [
  { v: "newest", label: "Nouveautés" },
  { v: "popular", label: "Populaires" },
  { v: "cheap", label: "Prix croissant" },
  { v: "expensive", label: "Prix décroissant" },
];

const AdminHomeLayout = ({ t }) => {
  const BUILTINS = [
    { k: "featured_count", label: "Produits en vedette", desc: "Section « En vedette »" },
    { k: "recommendations_count", label: "Recommandations", desc: "Section « Recommandé pour vous »" },
    { k: "recent_count", label: "Nouveautés", desc: "Section « Nouveautés »" },
  ];
  const [counts, setCounts] = useState(null);
  const [sections, setSections] = useState([]);
  const [cats, setCats] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/categories").then((r) => setCats(r.data)).catch(() => {});
    api.get("/settings/home").then((r) => {
      const d = r.data;
      setCounts({ featured_count: d.featured_count, recommendations_count: d.recommendations_count, recent_count: d.recent_count });
      setSections((d.sections || []).map((s) => ({ ...s, category: s.category || "all" })));
    }).catch(() => {});
  }, []);

  const clamp = (v) => Math.max(1, Math.min(50, parseInt(v || "1", 10) || 1));
  const addSection = () => setSections((s) => [...s, { id: `new-${Date.now()}`, title_fr: "", title_en: "", category: "all", sort: "newest", count: 8 }]);
  const updateSection = (i, patch) => setSections((s) => s.map((x, j) => (j === i ? { ...x, ...patch } : x)));
  const removeSection = (i) => setSections((s) => s.filter((_, j) => j !== i));

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        ...counts,
        sections: sections
          .filter((s) => (s.title_fr || "").trim())
          .map((s) => ({ id: String(s.id).startsWith("new-") ? undefined : s.id, title_fr: s.title_fr.trim(), title_en: (s.title_en || "").trim(), category: s.category === "all" ? "" : s.category, sort: s.sort, count: clamp(s.count) })),
      };
      const { data } = await api.put("/admin/settings/home", payload);
      setCounts({ featured_count: data.featured_count, recommendations_count: data.recommendations_count, recent_count: data.recent_count });
      setSections((data.sections || []).map((s) => ({ ...s, category: s.category || "all" })));
      toast.success("Réglages de la page d'accueil enregistrés");
    } catch (e) { toast.error(apiError(e)); } finally { setSaving(false); }
  };

  if (!counts) return null;
  return (
    <div className="max-w-3xl space-y-6" data-testid="home-layout-panel">
      <div className="bg-white border border-stone-200 rounded-xl p-6 space-y-6">
        <div className="flex items-center gap-3">
          <span className="w-11 h-11 rounded-xl bg-emerald-800 text-white grid place-items-center"><LayoutGrid className="w-6 h-6" /></span>
          <div>
            <p className="font-display font-extrabold text-xl leading-none">Produits par section</p>
            <p className="text-sm text-stone-500 mt-1">Choisissez combien de produits afficher dans chaque section principale (1 à 50).</p>
          </div>
        </div>
        <div className="space-y-4">
          {BUILTINS.map((s) => (
            <div key={s.k} className="flex items-center justify-between gap-4 border-b border-stone-100 pb-4 last:border-0 last:pb-0">
              <div>
                <p className="text-sm font-semibold">{s.label}</p>
                <p className="text-xs text-stone-400">{s.desc}</p>
              </div>
              <Input type="number" min={1} max={50} value={counts[s.k]} onChange={(e) => setCounts((c) => ({ ...c, [s.k]: clamp(e.target.value) }))} className="w-24 h-10 text-center font-semibold" data-testid={`home-count-${s.k}`} />
            </div>
          ))}
        </div>
      </div>

      <div className="bg-white border border-stone-200 rounded-xl p-6 space-y-5">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div>
            <p className="font-display font-extrabold text-xl leading-none">Sections personnalisées</p>
            <p className="text-sm text-stone-500 mt-1">Créez vos propres sections de produits (par catégorie, tri et nombre).</p>
          </div>
          <Button onClick={addSection} className="rounded-full bg-mango-600 hover:bg-mango-700" data-testid="home-add-section"><Plus className="w-4 h-4 mr-1" />Ajouter une section</Button>
        </div>
        {sections.length === 0 && <p className="text-sm text-stone-400">Aucune section personnalisée.</p>}
        <div className="space-y-4">
          {sections.map((s, i) => (
            <div key={s.id || i} className="border border-stone-200 rounded-xl p-4 space-y-3" data-testid={`home-section-${i}`}>
              <div className="flex items-center justify-between">
                <p className="text-xs font-semibold text-stone-400 uppercase tracking-wider">Section {i + 1}</p>
                <button onClick={() => removeSection(i)} className="text-red-500 hover:text-red-700" data-testid={`home-section-del-${i}`}><Trash2 className="w-4 h-4" /></button>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div><label className="text-xs text-stone-500">Titre (FR)</label><Input value={s.title_fr} onChange={(e) => updateSection(i, { title_fr: e.target.value })} className="h-9" data-testid={`home-section-fr-${i}`} /></div>
                <div><label className="text-xs text-stone-500">Titre (EN)</label><Input value={s.title_en} onChange={(e) => updateSection(i, { title_en: e.target.value })} className="h-9" data-testid={`home-section-en-${i}`} /></div>
                <div>
                  <label className="text-xs text-stone-500">Catégorie</label>
                  <Select value={s.category} onValueChange={(v) => updateSection(i, { category: v })}>
                    <SelectTrigger className="h-9" data-testid={`home-section-cat-${i}`}><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Toutes les catégories</SelectItem>
                      {cats.map((c) => <SelectItem key={c.key} value={c.key}>{c.fr}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-xs text-stone-500">Tri</label>
                    <Select value={s.sort} onValueChange={(v) => updateSection(i, { sort: v })}>
                      <SelectTrigger className="h-9" data-testid={`home-section-sort-${i}`}><SelectValue /></SelectTrigger>
                      <SelectContent>{SORT_OPTIONS.map((o) => <SelectItem key={o.v} value={o.v}>{o.label}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div><label className="text-xs text-stone-500">Nombre</label><Input type="number" min={1} max={50} value={s.count} onChange={(e) => updateSection(i, { count: clamp(e.target.value) })} className="h-9 text-center" data-testid={`home-section-count-${i}`} /></div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      <Button onClick={save} disabled={saving} className="rounded-full bg-emerald-800 hover:bg-emerald-900" data-testid="home-layout-save">Enregistrer</Button>
    </div>
  );
};

const Pager = ({ page, total, onPage, testid }) => {
  const pages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  if (total <= PAGE_SIZE) return null;
  return (
    <div className="flex items-center justify-between px-4 py-3 border-t border-stone-100 flex-wrap gap-2" data-testid={testid}>
      <p className="text-xs text-stone-400">Affichage {(page - 1) * PAGE_SIZE + 1}–{Math.min(page * PAGE_SIZE, total)} sur {total}</p>
      <div className="flex items-center gap-2">
        <Button variant="outline" size="sm" className="rounded-full" disabled={page <= 1} onClick={() => onPage(page - 1)} data-testid={`${testid}-prev`}>Précédent</Button>
        <span className="text-xs font-semibold px-1" data-testid={`${testid}-page`}>Page {page} / {pages}</span>
        <Button variant="outline" size="sm" className="rounded-full" disabled={page >= pages} onClick={() => onPage(page + 1)} data-testid={`${testid}-next`}>Suivant</Button>
      </div>
    </div>
  );
};

const AdminLocalisations = ({ t }) => {
  const PAGE_SIZE = 50;
  const [data, setData] = useState({ total: 0, items: [], page: 1 });
  const [filters, setFilters] = useState({});
  const [page, setPage] = useState(1);
  const [busy, setBusy] = useState(false);
  const [edit, setEdit] = useState(null);
  const fileRef = useRef(null);

  const load = useCallback((f = {}, p = 1) => {
    const qs = new URLSearchParams({ page: String(p), page_size: String(PAGE_SIZE) });
    ["region", "prefecture", "commune", "canton", "localite"].forEach((k) => f[k] && qs.set(k, f[k]));
    api.get(`/admin/localisations?${qs.toString()}`).then((r) => setData(r.data)).catch(() => {});
  }, []);
  useEffect(() => { load({}, 1); }, [load]);

  const applyFilters = (f) => { setFilters(f); setPage(1); load(f, 1); };
  const goPage = (p) => { setPage(p); load(filters, p); };
  const save = async () => {
    if (!edit.region?.trim()) return toast.error("Région requise");
    try {
      if (edit.id) await api.put(`/admin/localisations/${edit.id}`, edit);
      else await api.post("/admin/localisations", edit);
      toast.success(edit.id ? "Localité mise à jour" : t("locality_added"));
      setEdit(null); load(filters, page);
    } catch (e) { toast.error(apiError(e)); }
  };
  const delRow = async (id) => {
    try { await api.delete(`/admin/localisations/${id}`); toast.success(t("locality_deleted")); load(filters, page); }
    catch (e) { toast.error(apiError(e)); }
  };
  const importCsv = async (e) => {
    const file = e.target.files?.[0]; if (!file) return; setBusy(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data: res } = await api.post(`/admin/localisations/import?mode=replace`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`${t("import_success")} — ${res.imported} (${res.total})`); setFilters({}); setPage(1); load({}, 1);
    } catch (err) { toast.error(apiError(err)); } finally { setBusy(false); e.target.value = ""; }
  };

  const totalPages = Math.max(1, Math.ceil(data.total / PAGE_SIZE));
  return (
    <div className="space-y-6">
      <div className="bg-emerald-800 text-white rounded-xl p-5 flex flex-wrap items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <span className="w-11 h-11 rounded-xl bg-mango-600 grid place-items-center"><MapPin className="w-6 h-6" /></span>
          <div><p className="font-display font-extrabold text-2xl leading-none" data-testid="loc-total">{data.total}</p><p className="text-emerald-100 text-xs mt-1">{t("total_localities")}</p></div>
        </div>
        <input ref={fileRef} type="file" accept=".csv" className="hidden" data-testid="loc-import-input" onChange={importCsv} />
        <Button disabled={busy} onClick={() => fileRef.current?.click()} className="bg-mango-600 hover:bg-mango-700 rounded-full" data-testid="loc-import-btn"><Upload className="w-4 h-4 mr-2" />{t("import_csv")} ({t("replace_mode")})</Button>
      </div>

      <div className="bg-white border border-stone-200 rounded-xl p-4">
        <div className="flex items-center justify-between mb-3 gap-2 flex-wrap">
          <p className="font-semibold text-sm">{t("filter_by_location")}</p>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" className="rounded-full" onClick={() => applyFilters({})} data-testid="loc-filter-reset">{t("reset")}</Button>
            <Button size="sm" className="rounded-full" onClick={() => setEdit({ region: "", prefecture: "", commune: "", canton: "", localite: "" })} data-testid="loc-add-btn"><Plus className="w-4 h-4 mr-1" />{t("add_locality")}</Button>
          </div>
        </div>
        <LocationCascade value={filters} onChange={applyFilters} layout="row" />
      </div>

      <div className="bg-white border border-stone-200 rounded-xl overflow-hidden overflow-x-auto">
        <Table>
          <TableHeader><TableRow><TableHead>Région</TableHead><TableHead>Préfecture</TableHead><TableHead>Commune</TableHead><TableHead>Canton</TableHead><TableHead>Localité</TableHead><TableHead className="text-right">Actions</TableHead></TableRow></TableHeader>
          <TableBody>
            {data.items.map((it) => (
              <TableRow key={it.id} data-testid={`loc-row-${it.id}`}>
                <TableCell className="font-medium">{it.region}</TableCell>
                <TableCell className="text-stone-500">{it.prefecture}</TableCell>
                <TableCell className="text-stone-500">{it.commune}</TableCell>
                <TableCell className="text-stone-500">{it.canton}</TableCell>
                <TableCell className="text-stone-500">{it.localite}</TableCell>
                <TableCell className="text-right whitespace-nowrap">
                  <button onClick={() => setEdit(it)} className="text-emerald-700 hover:text-emerald-900 mr-3" data-testid={`loc-edit-${it.id}`}><Pencil className="w-4 h-4" /></button>
                  <button onClick={() => delRow(it.id)} className="text-red-500 hover:text-red-700" data-testid={`loc-del-${it.id}`}><Trash2 className="w-4 h-4" /></button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        {data.items.length === 0 && <p className="text-center text-xs text-stone-400 py-6">Aucune localité trouvée</p>}
        {data.total > 0 && (
          <div className="flex items-center justify-between px-4 py-3 border-t border-stone-100 flex-wrap gap-2" data-testid="loc-pagination">
            <p className="text-xs text-stone-400">Affichage {(page - 1) * PAGE_SIZE + 1}–{Math.min(page * PAGE_SIZE, data.total)} sur {data.total}</p>
            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm" className="rounded-full" disabled={page <= 1} onClick={() => goPage(page - 1)} data-testid="loc-prev">Précédent</Button>
              <span className="text-xs font-semibold px-1" data-testid="loc-page">Page {page} / {totalPages}</span>
              <Button variant="outline" size="sm" className="rounded-full" disabled={page >= totalPages} onClick={() => goPage(page + 1)} data-testid="loc-next">Suivant</Button>
            </div>
          </div>
        )}
      </div>

      <Dialog open={!!edit} onOpenChange={(o) => !o && setEdit(null)}>
        <DialogContent data-testid="loc-edit-dialog">
          <DialogHeader><DialogTitle>{edit?.id ? "Modifier la localité" : t("add_locality")}</DialogTitle></DialogHeader>
          <LocationEditor value={edit || {}} onChange={setEdit} />
          <Button onClick={save} className="rounded-full" data-testid="loc-save-btn">Enregistrer</Button>
        </DialogContent>
      </Dialog>
    </div>
  );
};

